import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  Inject,
} from '@angular/core'
import { AccountsSetValues } from '@helios/generic-components'
import {
  RootState,
  selectCurrentUser,
  selectSelectedReconDate,
  User,
} from '@helios/state-management'
import { UserPreferencesService } from '@helios/user-preferences'
import { Store } from '@ngrx/store'
import { ButtonVariant } from '@northfork/basics'
import { Subject } from 'rxjs'
import { filter, map, take, takeUntil, takeWhile } from 'rxjs/operators'
import {
  AccountListService,
  AccountOwnerField,
} from '../../services/account-list.service'
import { ConfigureBellModalService } from './configure-bell-modal/configure-bell-modal.service'
import {
  AccountListBellConfiguration,
  AccountOwners,
} from './omega-timestamp-status-bar.model'

const UPDATE_INTERVAL = 60 * 2_000 //Two Minutes

@Component({
  selector: 'hs-omega-timestamp-status',
  templateUrl: './omega-timestamp-status.component.html',
  styleUrls: ['./omega-timestamp-status.component.scss'],
  host: { class: 'hs-status-bar' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OmegaTimestampStatusComponent {
  private readonly destroySubject$ = new Subject<void>()

  @HostListener('click')
  async onClick() {
    const { accountOwnerField, userNames } = await this.config$
    this.setBellFilters(accountOwnerField, userNames)
    await this.updateCount()
    return false
  }

  numberOfAccountsWithoutOmega?: number
  toolTip = ''
  iconClass = 'mdi mdi-bell'
  buttonVariant: ButtonVariant = 'secondary'

  private config$: Promise<AccountListBellConfiguration> =
    this.getStoredConfig()

  constructor(
    private readonly accountListService: AccountListService,
    private readonly userPreferencesService: UserPreferencesService,
    private readonly configureBellModalService: ConfigureBellModalService,
    private readonly store: Store<RootState>,
    @Inject('AccountSetValuesGetter')
    private readonly getAccountSetValues: () => Promise<AccountsSetValues>,
    @Inject('BellFilterSetter')
    private readonly setBellFilters: (
      field: AccountOwnerField,
      userNames: string[],
    ) => void,
    private readonly cd: ChangeDetectorRef,
  ) {}

  async init() {
    await this.updateCount()
    this.cd.detectChanges()
    this.updateCountOnInterval()
  }

  destroy() {
    this.destroySubject$.next()
    this.destroySubject$.complete()
  }

  private async updateCountOnInterval() {
    if (this.destroySubject$.isStopped) {
      return
    }

    await this.updateCount()
    setTimeout(() => this.updateCountOnInterval(), UPDATE_INTERVAL)
  }

  private async getCount() {
    const reconDate = await this.store
      .select(selectSelectedReconDate)
      .pipe(takeUntil(this.destroySubject$), take(1))
      .toPromise()
    if (reconDate == null) {
      return 0
    }

    const { userNames, accountOwnerField } = await this.config$

    return this.accountListService
      .getNeedsOmegaTsCount(reconDate, userNames, accountOwnerField)
      .pipe(take(1), takeUntil(this.destroySubject$))
      .toPromise()
  }

  private async updateCount() {
    const count = await this.getCount()

    if (count === this.numberOfAccountsWithoutOmega) {
      return
    }

    this.numberOfAccountsWithoutOmega = count
    this.toolTip = `${count} accounts without omega timestamp`

    if (count > 0) {
      this.iconClass = 'mdi mdi-bell-alert'
      this.buttonVariant = 'danger'
    } else {
      this.iconClass = 'mdi mdi-bell-check'
      this.buttonVariant = 'success'
    }

    this.cd.detectChanges()
  }

  private async getStoredConfig(): Promise<AccountListBellConfiguration> {
    const storedConfig =
      this.userPreferencesService.getAccountListBellConfiguration()
    if (storedConfig != null) {
      return storedConfig
    }

    const currentUser = await this.store
      .select(selectCurrentUser)
      .pipe(
        takeUntil(this.destroySubject$),
        takeWhile(user => user == null, true),
        filter((user): user is User => user != null),
        take(1),
        map(user => user.nickname),
      )
      .toPromise()

    const config: AccountListBellConfiguration = {
      userNames: currentUser ? [currentUser] : [],
      accountOwnerField: 'reconciler',
    }

    this.userPreferencesService.setAccountListBellConfiguration(
      config.userNames,
      config.accountOwnerField,
    )

    return config
  }

  async toggleConfigureModal() {
    const newConfig =
      await this.configureBellModalService.toggleConfigureBellModal(
        await this.config$,
        await this.getAccountOwners(),
      )

    if (newConfig == null) {
      return
    }

    this.config$ = Promise.resolve(newConfig)
    this.userPreferencesService.setAccountListBellConfiguration(
      newConfig.userNames,
      newConfig.accountOwnerField,
    )

    await this.updateCount()
  }

  private async getAccountOwners(): Promise<AccountOwners> {
    const accountSetValues = await this.getAccountSetValues()
    return {
      managers: accountSetValues.managers,
      opsManagers: accountSetValues.opsManagers,
      reconcilers: accountSetValues.reconcilers,
    }
  }
}
